/**
 * Adapter around `@anam-ai/js-sdk`.
 *
 * Anam handles the full TTS + photoreal-face render in their cloud and
 * streams it to a `<video>` element via WebRTC. We use it as a
 * "bring-your-own-LLM" speaker: text from our n8n LLM is fed in via
 * `createTalkMessageStream` and Anam speaks it verbatim.
 */
import type AnamClient from '@anam-ai/js-sdk/dist/module/AnamClient';
import type { TalkMessageStream } from '@anam-ai/js-sdk/dist/module/types/TalkMessageStream';
import * as anamEvents from './anamEvents';
import {
  fetchSessionToken,
  SessionTokenError,
  type SessionTokenErrorCode,
} from './fetchSessionToken';

export { SessionTokenError, type SessionTokenErrorCode };

let client: AnamClient | null = null;
let connecting: Promise<void> | null = null;
let disposing: Promise<void> | null = null;
let initEpoch = 0;

export const anamClient = {
  /** Fetch a session token from our BFF and create the SDK client. Idempotent. */
  async init(opts: { apiEndpoint: string; institutionId: string }): Promise<void> {
    const myEpoch = initEpoch;
    if (disposing) {
      await disposing.catch(() => undefined);
    }
    if (initEpoch !== myEpoch || client) return;
    if (connecting) return connecting;
    connecting = (async () => {
      const sdk = await import('@anam-ai/js-sdk');
      const token = await fetchSessionToken(opts.apiEndpoint, opts.institutionId);
      const c = sdk.createClient(token, { disableInputAudio: false });
      if (initEpoch !== myEpoch) {
        await c.stopStreaming().catch(() => undefined);
        return;
      }
      client = c;
      anamEvents.attachListeners(client);
    })();
    try {
      await connecting;
    } finally {
      connecting = null;
    }
  },

  async attach(videoElement: HTMLVideoElement): Promise<void> {
    if (!client) throw new Error('anamClient.attach: call init() first');
    const streams = await client.stream();
    const combined = new MediaStream();
    for (const s of streams) {
      for (const t of s.getTracks()) combined.addTrack(t);
    }
    videoElement.srcObject = combined;
    try {
      client.muteInputAudio();
    } catch {
      // Mic permission may still be PENDING on first stream.
    }
  },

  async dispose(): Promise<void> {
    initEpoch += 1;
    if (disposing) return disposing;
    disposing = (async () => {
      if (connecting) {
        await connecting.catch(() => undefined);
      }
      await client?.stopStreaming().catch(() => undefined);
      client = null;
      anamEvents.resetEvents();
    })();
    try {
      await disposing;
    } finally {
      disposing = null;
    }
  },

  async talk(text: string): Promise<void> {
    if (!client) return;
    try {
      await client.talk(text);
    } finally {
      anamEvents.setSpeaking(false);
    }
  },

  beginTurn(correlationId?: string): TalkMessageStream | null {
    if (!client) return null;
    anamEvents.clearPersonaSpeechText();
    anamEvents.setSpeaking(true);
    return client.createTalkMessageStream(correlationId);
  },

  async pushChunk(stream: TalkMessageStream | null, text: string, endOfSpeech = false): Promise<void> {
    if (!stream || !stream.isActive()) return;
    await stream.streamMessageChunk(text, endOfSpeech);
  },

  async endTurn(stream: TalkMessageStream | null): Promise<void> {
    if (!stream || !stream.isActive()) return;
    await stream.endMessage().catch(() => undefined);
  },

  interrupt(): void {
    if (!client) return;
    try {
      client.interruptPersona();
    } catch {
      // Not currently speaking — safe to ignore.
    }
    anamEvents.setSpeaking(false);
  },

  setMicEnabled(enabled: boolean): void {
    if (!client) return;
    if (enabled) client.unmuteInputAudio();
    else client.muteInputAudio();
  },

  onUserTranscript: anamEvents.onUserTranscript,
  onSpeakingChange: anamEvents.onSpeakingChange,
  isSpeaking: anamEvents.isSpeaking,
  onConnectionClosed: anamEvents.onConnectionClosed,
  onPersonaSpeechText: anamEvents.onPersonaSpeechText,
  getPersonaSpeechText: anamEvents.getPersonaSpeechText,
  clearPersonaSpeechText: anamEvents.clearPersonaSpeechText,
  onInputAudioStream: anamEvents.onInputAudioStream,
  getInputAudioStream: anamEvents.getInputAudioStream,

  __reset(): void {
    initEpoch += 1;
    client = null;
    connecting = null;
    disposing = null;
    anamEvents.resetEvents();
    anamEvents.clearListeners();
  },
};
